import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { SelectUsersComponent, SelectUsersData } from '../select-users/select-users.component';
import { ContactSelection } from '../model';
import { AdminsApi, Person } from 'src/app/hickory-api';
import { AuthService } from '../authentication/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { catchError, finalize, Subject, takeUntil, tap, throwError } from 'rxjs';
import { ApiError } from '../model/utility';
import { SB_PANEL_ERROR } from '../snackbar.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-admin-form-field',
  templateUrl: './admin-form-field.component.html',
  styleUrl: './admin-form-field.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AdminFormFieldComponent implements OnInit, OnDestroy {
  public readonly locale: L10nLocale = inject(L10N_LOCALE);
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly adminApi = inject(AdminsApi);
  private readonly sb = inject(MatSnackBar);
  private readonly translation = inject(L10nTranslationService);

  @Input() adminId: number;
  @Input() label: string;
  @Input() hint: string;
  @Input() selectLabel = 'Integrations.SelectSender';
  @Output() adminChange = new EventEmitter<Person>();
  selectedAdmin: Person;
  adminLoading = false;
  destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.getAdmin();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectAdmin() {
    const data: SelectUsersData = {
      selectedContacts: this.selectedAdmin ? [new ContactSelection(true).withAdmin(this.selectedAdmin)] : [],
      isSingleSelect: true,
    };

    this.dialog
      .open(SelectUsersComponent, { data: data, minWidth: '600px', ariaLabel: 'Select a user' })
      .afterClosed()
      .subscribe((result) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const list: ContactSelection[] = result?.selectedContacts || [];
        this.selectedAdmin = list.length ? list[0].toAdmin() : this.selectedAdmin;
        this.adminChange.emit(this.selectedAdmin);
      });
  }

  getAdmin() {
    this.adminLoading = true;
    const currentUser = this.authService.user.snapshot;
    const senderId = this.adminId || currentUser?.id;

    if (senderId === currentUser?.id) {
      this.selectedAdmin = {
        id: senderId,
        name: currentUser?.name,
      };
      this.adminChange.emit(this.selectedAdmin);
      this.adminLoading = false;
      return;
    }

    this.adminApi
      .getAdmins([senderId])
      .pipe(
        tap((response) => {
          if (response?.items?.length) {
            this.selectedAdmin = response?.items[0];
            this.adminChange.emit(this.selectedAdmin);
          }
        }),
        catchError((error: ApiError) => {
          const message = this.translation.translate('Integrations.FetchDataFailed');
          const action = this.translation.translate('LoadingSpinner.Retry');
          const sbRef = this.sb.open(message, action, { duration: 10000, panelClass: SB_PANEL_ERROR });
          sbRef.onAction().subscribe(() => {
            this.getAdmin();
          });

          return throwError(() => error);
        }),
        takeUntil(this.destroy$),
        finalize(() => {
          this.adminLoading = false;
        }),
      )
      .subscribe();
  }

  getAdminDisplayName(): string {
    const name = this.selectedAdmin?.name;
    if (name) {
      return name.familyName + ', ' + name.givenName;
    } else {
      return null;
    }
  }

  getLabel(): string {
    return this.label || this.translation.translate('Integrations.AlertSender');
  }
}
